import { type TrackReference, useTracks, VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { useEffect, useRef, useState } from 'react'

import { playTestTone } from '../audioOutput'
import { newFailureId, report } from '../diagnostics'
import { t } from '../i18n'
import type { DevicesApi } from '../useDevices'
import type { PipApi } from '../usePictureInPicture'
import type { Verdict } from './setupVerdicts'

type Focus = 'mic' | 'cam' | 'speaker' | 'share' | 'net'
type Action = NonNullable<Verdict['action']>

export function SetupHub({
  checks,
  devices,
  pip,
  credential,
  focus,
  onClose,
  returnFocusTo,
  onAction,
}: {
  checks: {
    mic: Verdict
    cam: Verdict
    speaker: Verdict
    share: Verdict
    connection: Verdict
    level: number | null
  }
  devices: DevicesApi
  pip: PipApi
  credential: { code?: string | null; reconnectToken?: string | null }
  focus?: Focus
  onClose: () => void
  returnFocusTo: React.RefObject<HTMLButtonElement>
  onAction: (action: Action) => void
}) {
  const panel = useRef<HTMLDivElement>(null)
  const [supportCode, setSupportCode] = useState<string | null>(null)
  const cameraRef = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }], {
    onlySubscribed: false,
  }).find((ref): ref is TrackReference => ref.participant.isLocal && Boolean(ref.publication))
  useEffect(() => {
    panel.current?.focus()
    return () => returnFocusTo.current?.focus()
  }, [returnFocusTo])
  const copyCode = () => {
    const id = newFailureId()
    const snapshot = `help=snapshot;mic=${checks.mic.status};cam=${checks.cam.status};spk=${checks.speaker.status};share=${checks.share.status};net=${checks.connection.status};pip=${pip.active ? 'active' : pip.canEnter ? 'ready' : 'off'}`
    report(credential, { stage: 'permissions', outcome: 'ok', failureId: id, permission: snapshot })
    void navigator.clipboard?.writeText?.(`#${id}`).catch(() => undefined)
    setSupportCode(id)
  }
  const rows: Array<{
    id: Focus | 'pip'
    label: string
    verdict: Verdict
    extra?: React.ReactNode
  }> = [
    {
      id: 'mic',
      label: t('help.row.mic'),
      verdict: checks.mic,
      extra: (
        <>
          <meter className="setup-hub__meter" min="0" max="1" value={checks.level ?? 0} />
          <DeviceSelect kind="audioinput" devices={devices} />
        </>
      ),
    },
    {
      id: 'cam',
      label: t('help.row.cam'),
      verdict: checks.cam,
      extra: cameraRef ? <VideoTrack trackRef={cameraRef} className="setup-hub__camera" /> : null,
    },
    {
      id: 'speaker',
      label: t('help.row.speaker'),
      verdict: checks.speaker,
      extra: (
        <>
          <button
            type="button"
            className="setup-hub__test"
            onClick={async () => {
              try {
                await playTestTone(devices.active.audiooutput)
              } catch {
                /* the browser keeps the user-visible state */
              }
            }}
          >
            {t('help.testTone')}
          </button>
          <DeviceSelect kind="audiooutput" devices={devices} />
        </>
      ),
    },
    { id: 'share', label: t('help.row.share'), verdict: checks.share },
    { id: 'net', label: t('help.row.connection'), verdict: checks.connection },
    {
      id: 'pip',
      label: t('help.row.pip'),
      verdict:
        pip.mode === 'none'
          ? { status: 'unknown', key: 'help.pip.unavailable' }
          : pip.active
            ? { status: 'ok', key: 'help.pip.active' }
            : pip.autoSuspect
              ? { status: 'warn', key: 'pip.autoBlockedTitle', action: 'pipHelp' }
              : { status: 'ok', key: 'help.pip.ready' },
    },
  ]
  return (
    <aside
      className="setup-hub"
      role="dialog"
      aria-modal="false"
      aria-labelledby="setup-hub-title"
      tabIndex={-1}
      ref={panel}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
    >
      <header className="setup-hub__head">
        <h2 id="setup-hub-title">{t('help.title')}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('roster.close')}
          title={t('roster.close')}
        >
          ×
        </button>
      </header>
      <div className="setup-hub__rows">
        {rows.map((row) => (
          <section
            className={`setup-hub__row ${focus === row.id ? 'setup-hub__row--focus' : ''}`}
            key={row.id}
          >
            <span
              className={`setup-hub__dot setup-hub__dot--${row.verdict.status}`}
              aria-label={row.verdict.status}
            />
            <div className="setup-hub__copy">
              <h3>{row.label}</h3>
              <p>{t(row.verdict.key)}</p>
              {row.extra}
            </div>
            {row.id === 'pip' ? (
              pip.mode === 'none' || pip.active ? null : (
                <>
                  {row.verdict.action ? (
                    <button type="button" onClick={() => onAction(row.verdict.action!)}>
                      {t('help.fix')}
                    </button>
                  ) : null}
                  {pip.canEnter ? (
                    <button type="button" onClick={pip.toggle}>
                      {t('pip.enter')}
                    </button>
                  ) : null}
                </>
              )
            ) : row.verdict.action ? (
              <button type="button" onClick={() => onAction(row.verdict.action!)}>
                {t('help.fix')}
              </button>
            ) : null}
          </section>
        ))}
      </div>
      <footer className="setup-hub__foot">
        <button type="button" onClick={copyCode}>
          {t('help.copyCode')}
        </button>
        {supportCode ? <p>{t('help.readCode').replace('{code}', `#${supportCode}`)}</p> : null}
      </footer>
    </aside>
  )
}

function DeviceSelect({
  kind,
  devices,
}: {
  kind: 'audioinput' | 'audiooutput'
  devices: DevicesApi
}) {
  if (kind === 'audiooutput' && !devices.supportsSpeakerSelection) return null
  if (!devices.devices[kind].length) return null
  return (
    <select
      aria-label={t(kind === 'audioinput' ? 'devices.microphone' : 'devices.speaker')}
      value={devices.active[kind]}
      onChange={(event) => void devices.select(kind, event.target.value)}
    >
      {devices.devices[kind].map((device) => (
        <option key={device.deviceId} value={device.deviceId}>
          {device.label || t('devices.systemDefault')}
        </option>
      ))}
    </select>
  )
}
